import axios, { AxiosHeaders } from "axios";
import PermissionCommand from "./request-contracts/permission-command";
import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import BulkPermissionCommand from "permissions/_api/request-contracts/bulk-permission-command";
import Permission from "permissions/permission";
import Session from "users/__session/session";
import PermissionSchema, { PermissionJSON } from "permissions/_schemas/permission-schema";
import PermissionsByGranteeSchema, {
  PermissionsByGranteeJSON,
} from "permissions/by-grantee/_schemas/permissions-by-grantee-schema";

export default class PermissionsAPIService {
  public constructor(session: Readonly<Session>) {
    if (!session.authToken) throw new Error("Cannot create PermissionsAPIService without session.");
    this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  public async addPermission(permission: Permission, abortController?: AbortController): Promise<PermissionJSON> {
    const response = await axios.post(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions`,
      new PermissionCommand(permission),
      {
        headers: this.headersWithJson(),
        signal: abortController?.signal,
      },
    );

    return PermissionSchema.validateJSON(response.data);
  }

  public async bulkAddPermissions(
    permissions: Permission[],
    abortController?: AbortController,
  ): Promise<PermissionJSON[]> {
    const response = await axios.post(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/bulk`,
      new BulkPermissionCommand(permissions),
      {
        headers: this.headersWithJson(),
        signal: abortController?.signal,
      },
    );

    const responseData = response.data.map((p: unknown) => PermissionSchema.validateJSON(p));
    return responseData;
  }

  public async getPermissions(
    query: PermissionQuery,
    abortController?: AbortController,
  ): Promise<PaginatedResponse<PermissionJSON>> {
    if (query.permissionView === PermissionView.Grantee) {
      throw new Error(
        "The getPermissions method is not supported when the 'Grantee' permissionView is specified in the permission query.",
      );
    }

    const response = await axios.get(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions?${query.searchParams.toString()}`,
      {
        headers: this.headers,
        signal: abortController?.signal,
      },
    );

    const responseData = response.data.map((p: unknown) => PermissionSchema.validateJSON(p));

    return PaginatedResponse.from<PermissionJSON>(responseData, response.headers["x-pagination"]);
  }

  public async getPermissionsByGrantee(
    query: PermissionQuery,
    abortController?: AbortController,
  ): Promise<PaginatedResponse<PermissionsByGranteeJSON>> {
    if (query.permissionView !== PermissionView.Grantee) {
      throw new Error(
        "The getPermissionsByGrantee method is not supported if the 'Grantee' permissionView is NOT specified in the permission query.",
      );
    }

    const response = await axios.get(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions?${query.searchParams.toString()}`,
      {
        headers: this.headers,
        signal: abortController?.signal,
      },
    );

    const responseData = response.data.map((p: unknown) => PermissionsByGranteeSchema.validateJSON(p));

    return PaginatedResponse.from<PermissionsByGranteeJSON>(responseData, response.headers["x-pagination"]);
  }

  public async disablePermission(permission: Permission, abortController?: AbortController): Promise<PermissionJSON> {
    if (!permission.id) {
      throw new Error("Permission ID is required to disable a permission.");
    }
    const response = await axios.delete(`${AttorneyHubAPIService.apiBaseUrl}/permissions/${permission.id.value}`, {
      headers: this.headers,
      signal: abortController?.signal,
    });

    return PermissionSchema.validateJSON(response.data);
  }

  public async enablePermission(permission: Permission, abortController?: AbortController): Promise<PermissionJSON> {
    if (!permission.id) {
      throw new Error("Permission ID is required to enable a permission.");
    }
    const response = await axios.post(
      `${AttorneyHubAPIService.apiBaseUrl}/permissions/${permission.id}/enable`,
      {},
      {
        headers: this.headers,
        signal: abortController?.signal,
      },
    );

    return PermissionSchema.validateJSON(response.data);
  }

  private readonly headers: AxiosHeaders = new AxiosHeaders();

  private headersWithJson(): AxiosHeaders {
    return this.headers.concat({ "Content-Type": "application/json-patch+json" });
  }
}

export class PermissionsAPIServiceError extends Error {
  public methodName: string;
  public error: unknown;

  constructor(methodName: string, error: unknown) {
    super(`Error in PermissionsAPIService.${methodName}: ${error}`);
    this.methodName = methodName;
    this.error = error;
  }
}
